import { useState } from "react";
import axios from "axios";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowRight, Check, Loader2, PartyPopper, MapPin, Link2 } from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const WaitlistForm = ({ idPrefix = "waitlist", onJoined }) => {
  const [email, setEmail] = useState("");
  const [city, setCity] = useState("");
  const [state, setState] = useState("idle");
  const [referralCode, setReferralCode] = useState(null);

  const submit = async (e) => {
    e.preventDefault();
    if (!email || state === "loading") return;
    setState("loading");
    const ref = new URLSearchParams(window.location.search).get("ref");
    try {
      const res = await axios.post(`${API}/waitlist`, {
        email,
        city: city || undefined,
        ref: ref || undefined,
      });
      setReferralCode(res.data.referral_code || null);
      if (res.data.status === "exists") {
        setState("exists");
      } else {
        setState("success");
        toast.success("Benvenuto nella famiglia! Controlla la tua email.");
        onJoined?.(res.data.count);
      }
    } catch (err) {
      setState("idle");
      const detail = err?.response?.data?.detail;
      toast.error(
        Array.isArray(detail) ? "Inserisci un indirizzo email valido." : "Qualcosa è andato storto. Riprova."
      );
    }
  };

  if (state === "success" || state === "exists") {
    return (
      <motion.div
        initial={{ opacity: 0, y: 12, scale: 0.98 }}
        animate={{ opacity: 1, y: 0, scale: 1 }}
        transition={{ duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
        className="w-full max-w-xl"
      >
        <div
          data-testid={`${idPrefix}-success-message`}
          className="flex items-center gap-3 bg-[#1A1A1A] text-[#F9F8F6] rounded-full pl-4 pr-6 py-4"
        >
          <span className="flex items-center justify-center w-9 h-9 rounded-full bg-[#FF6B35] shrink-0">
            {state === "exists" ? <PartyPopper className="w-4 h-4" /> : <Check className="w-4 h-4" strokeWidth={3} />}
          </span>
          <p className="text-sm sm:text-base font-medium">
            {state === "exists"
              ? "Sei già nella lista: ci vediamo al lancio!"
              : "Sei dentro! Ti abbiamo appena scritto una email."}
          </p>
        </div>
        {referralCode && (
          <p
            data-testid={`${idPrefix}-referral-link`}
            className="mt-3 flex items-center gap-2 text-xs sm:text-sm font-medium text-[#5C5C5C] pl-2"
          >
            <Link2 className="w-3.5 h-3.5 text-[#FF6B35] shrink-0" />
            Il tuo link invito:
            <span className="font-bold text-[#FF6B35] break-all">fuffyfam.com/?ref={referralCode}</span>
          </p>
        )}
      </motion.div>
    );
  }

  return (
    <div className="w-full max-w-xl">
      <form
        onSubmit={submit}
        data-testid={`${idPrefix}-form`}
        className="flex items-center bg-white border border-[#1A1A1A]/10 rounded-full p-1.5 w-full shadow-[0_8px_30px_rgba(26,26,26,0.06)] focus-within:ring-2 focus-within:ring-[#FF6B35] focus-within:ring-offset-2 focus-within:ring-offset-[#F9F8F6] transition-shadow duration-300"
      >
        <input
          type="email"
          required
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="La tua email"
          data-testid={`${idPrefix}-email-input`}
          aria-label="Indirizzo email"
          className="flex-1 min-w-0 bg-transparent px-4 sm:px-5 py-3 text-sm sm:text-base text-[#1A1A1A] placeholder:text-[#5C5C5C]/60 outline-none"
        />
        <motion.button
          type="submit"
          whileHover={{ scale: 1.03 }}
          whileTap={{ scale: 0.97 }}
          disabled={state === "loading"}
          data-testid={`${idPrefix}-submit-button`}
          className="flex items-center gap-2 bg-[#FF6B35] hover:bg-[#E55A2B] text-white font-semibold text-sm sm:text-base rounded-full px-5 sm:px-7 py-3 transition-colors duration-300 disabled:opacity-70 shrink-0"
        >
          <AnimatePresence mode="wait" initial={false}>
            {state === "loading" ? (
              <motion.span key="loading" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
                <Loader2 className="w-4 h-4 animate-spin" />
              </motion.span>
            ) : (
              <motion.span
                key="idle"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                className="flex items-center gap-2"
              >
                <span className="hidden sm:inline">Unisciti alla lista</span>
                <span className="sm:hidden">Unisciti</span>
                <ArrowRight className="w-4 h-4" />
              </motion.span>
            )}
          </AnimatePresence>
        </motion.button>
      </form>
      <div className="mt-3 flex items-center gap-2 pl-2">
        <MapPin className="w-3.5 h-3.5 text-[#FF6B35] shrink-0" />
        <input
          type="text"
          value={city}
          onChange={(e) => setCity(e.target.value)}
          placeholder="La tua città (facoltativo)"
          data-testid={`${idPrefix}-city-input`}
          aria-label="La tua città (facoltativo)"
          maxLength={60}
          className="bg-transparent text-xs sm:text-sm text-[#1A1A1A] placeholder:text-[#5C5C5C]/50 outline-none border-b border-transparent focus:border-[#FF6B35]/40 transition-colors duration-300 py-1 w-56"
        />
      </div>
    </div>
  );
};
